import type { Health } from './api';
import { auth0SettingsOf } from './api';

/**
 * Data-only rules for which sign-in the app offers and which screen a
 * session state leads to. Kept apart from the components so the client tests
 * can cover them without a browser.
 */

/**
 * preview      local preview: the account selector (never offered by a hosted server)
 * hosted       the team demo: Auth0 sign-in, fictional records
 * unavailable  a hosted server whose configuration is incomplete; nothing is served
 * none         no sign-in is connected
 */
export type AppMode = 'preview' | 'hosted' | 'unavailable' | 'none';

export function appModeOf(health: Health): AppMode {
  if (health.auth?.mode === 'unavailable') return 'unavailable';
  if (health.auth?.mode === 'password') return 'hosted';
  if (health.auth?.mode === 'auth0') return auth0SettingsOf(health) ? 'hosted' : 'unavailable';
  return health.devIdentity ? 'preview' : 'none';
}

/** What the hosted demo shows in place of registry content, or null to show the content. */
export type HostedGate =
  'checking' | 'signed-out' | 'sign-in-failed' | 'session-ended' | 'not-authorized' | null;

export function hostedGateOf(
  sessionStatus: 'loading' | 'signed-out' | 'signed-in' | 'error' | undefined,
  account: { state: 'idle' | 'loading' | 'ready' | 'error'; status?: number },
): HostedGate {
  if (sessionStatus === undefined || sessionStatus === 'loading') return 'checking';
  if (sessionStatus === 'error') return 'sign-in-failed';
  if (sessionStatus === 'signed-out') return 'signed-out';
  if (account.state === 'error' && account.status === 401) return 'session-ended';
  if (account.state === 'error' && account.status === 403) return 'not-authorized';
  return null;
}

/**
 * Where to go after sign-in: only a path inside this app. Anything else
 * (another origin, a protocol-relative URL, leftover Auth0 callback
 * parameters) falls back to the home page.
 */
export function safeReturnPath(raw: unknown): string {
  if (
    typeof raw !== 'string' ||
    !raw.startsWith('/') ||
    raw.startsWith('//') ||
    raw.includes('\\')
  ) {
    return '/';
  }
  let url: URL;
  try {
    url = new URL(raw, 'https://app.invalid');
  } catch {
    return '/';
  }
  if (url.origin !== 'https://app.invalid') return '/';
  const callback = ['code', 'state', 'error', 'error_description'];
  // The query is kept exactly as written unless it carries callback parameters.
  if (!callback.some((name) => url.searchParams.has(name))) return url.pathname + url.search;
  for (const name of callback) url.searchParams.delete(name);
  const query = url.searchParams.toString();
  return url.pathname + (query ? `?${query}` : '');
}
